import React, { useEffect, useState } from "react";
import "./AddPropertyForm.css";
import Sidebar from "../components/Sidebar";
import { Box, useMediaQuery, useTheme, Button, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import ApiService from "../../../config/api";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AdminNavbar from "../components/AdminNavbar";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowBack as ArrowBackIcon, } from '@mui/icons-material';

const AddPropertyForm = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const [loading, setLoading] = useState(false);

  const toggleDrawer = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const predefinedAmenities = [
    "Gym",
    "Swimming Pool",
    "Parking",
    "Garden",
    "Clubhouse",
    "Playground",
    "24/7 Security",
    "Power Backup",
    "Elevator",
    "Wi-Fi Connectivity",
  ];

  const predefinedNearbyPlaces = [
    "Shopping Mall",
    "Hospital",
    "School",
    "Public Transport",
    "Restaurant",
    "Bank",
    "Supermarket",
    "Park",
    "Gym",
    "Cinema",
  ];

  const [property, setProperty] = useState({
    title: "",
    description: "",
    price: "",
    discountPrice: "",
    floor: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    latitude: "",
    longitude: "",
    builtInArea: "",
    numberOfBedrooms: "",
    numberOfBathrooms: "",
    constructionYear: "",
    amenities: [],
    nearbyPlaces: [],
    images: [],
    images360: "",
    threeDModels: "",
    availableQuantity: "",
    totalQuantity: "",
    brochure: "",
    locality: "",
    projectStatus: "",
    onDemand: "",
  });

  const [errorMessages, setErrorMessages] = useState({});
  const [nearbyPlace, setNearbyPlace] = useState("");
  const [amenity, setAmenity] = useState("");
  const [imagePreviews, setImagePreviews] = useState([]);
  const [localities, setLocalities] = useState([]);

  const navigate = useNavigate();

  const validatePropertyForm = (property) => {
    const errors = {};
    let isValid = true;

    if (!property.title.trim()) {
      errors.title = "Title is required";
      isValid = false;
    }

    if (!property.description.trim()) {
      errors.description = "Description is required";
      isValid = false;
    }

    if (property.price === "" || property.price < 0) {
      errors.price = "Price must be a non-negative number";
      isValid = false;
    }

    if (property.discountPrice === "" || property.discountPrice < 0) {
      errors.discountPrice = "Discount Price must be a non-negative number";
      isValid = false;
    }

    if (property.floor === "" || property.floor < 0) {
      errors.floor = "Floor must be a non-negative number";
      isValid = false;
    }

    if (property.area === "" || property.area < 0) {
      errors.area = "Area must be a non-negative number";
      isValid = false;
    }

    if (!property.city.trim()) {
      errors.city = "City is required";
      isValid = false;
    }

    if (!property.state.trim()) {
      errors.state = "State is required";
      isValid = false;
    }

    if (!property.country.trim()) {
      errors.country = "Country is required";
      isValid = false;
    }

    if (!property.pincode.trim()) {
      errors.pincode = "Pincode is required";
      isValid = false;
    }

    if (property.latitude === "" || property.latitude < 0) {
      errors.latitude = "Latitude must be a non-negative number";
      isValid = false;
    }

    if (property.longitude === "" || property.longitude < 0) {
      errors.longitude = "Longitude must be a non-negative number";
      isValid = false;
    }

    if (property.numberOfBedrooms === "" || property.numberOfBedrooms < 0) {
      errors.numberOfBedrooms =
        "Number of Bedrooms must be a non-negative number";
      isValid = false;
    }

    if (property.numberOfBathrooms === "" || property.numberOfBathrooms < 0) {
      errors.numberOfBathrooms =
        "Number of Bathrooms must be a non-negative number";
      isValid = false;
    }

    if (property.constructionYear === "" || property.constructionYear < 0) {
      errors.constructionYear =
        "Number of Bathrooms must be a non-negative number";
      isValid = false;
    }

    if (!property.constructionYear.trim()) {
      errors.constructionYear = "Construction Year is required";
      isValid = false;
    }

    if (!property.locality) {
      errors.locality = "Locality is required";
      isValid = false;
    }

    if (!property.projectStatus) {
      errors.projectStatus = "Project Status is required";
      isValid = false;
    }

    return { isValid, errors };
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "brochure") {
      setProperty((prevProperty) => ({
        ...prevProperty,
        [name]: files[0],
      }));
      return;
    }

    const isNumericField =
      name.includes("number") ||
      ["price", "discountPrice", "floor", "area", "latitude", "longitude", "agentPhone", "BuildUpArea"].includes(name);

    const newValue = isNumericField
      ? Math.max(0, Number(value))
      : value;

    setProperty((prevProperty) => ({
      ...prevProperty,
      [name]: newValue,
    }));

    // Remove error if the field is now valid
    if (errorMessages[name] && value.trim()) {
      setErrorMessages((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };


  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = [...property.images, ...files];
    const newPreviews = newImages.map((file) => URL.createObjectURL(file));
    setProperty({ ...property, images: newImages });
    setImagePreviews(newPreviews);
  };

  const handleRemoveImage = (index) => {
    const newImages = [...property.images];
    const newPreviews = [...imagePreviews];

    newImages.splice(index, 1);
    newPreviews.splice(index, 1);

    setProperty({ ...property, images: newImages });
    setImagePreviews(newPreviews);
  };

  const handle360ImageChange = (e) => {
    const file = e.target.files[0];
    setProperty({
      ...property,
      images360: URL.createObjectURL(file),
    });
  };

  const handle3DModelChange = (e) => {
    const file = e.target.files[0];
    setProperty({
      ...property,
      threeDModels: URL.createObjectURL(file),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { isValid, errors } = validatePropertyForm(property);
      if (!isValid) {
        setErrorMessages(errors);
        return;
      }

      setLoading(true);

      const formdata = new FormData();
      for (const key in property) {
        let value = property[key];

        if (key === "onDemand") {
          value = value === "true";
        }

        if (Array.isArray(value)) {
          value.forEach((v, index) => {
            if (key === "images") {
              formdata.append(`${key}`, v);
            } else {
              formdata.append(`${key}[${index}]`, v);
            }
          });
        } else {
          if (key === "brochure" && value) {
            formdata.append("brochure", value);
          } else if (key === "localityId") {
            formdata.append("localityId", value);
          } else {
            formdata.append(key, value);
          }
        }
      }
      console.log("📦 Payload (FormData):");
      for (let pair of formdata.entries()) {
        console.log(pair[0], ":", pair[1]);
      }

      const response = await ApiService.post(
        "admin/create-properties",
        formdata,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success(response.data.message);
    } catch (error) {
      toast.error("Error creating property");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };




  const handleRemovePlace = (index) => {
    setProperty((prevProperty) => {
      const updatedPlaces = [...prevProperty.nearbyPlaces];
      updatedPlaces.splice(index, 1);
      return { ...prevProperty, nearbyPlaces: updatedPlaces };
    });
  };

  const handleRemoveAmenity = (index) => {
    setProperty((prevProperty) => ({
      ...prevProperty,
      amenities: prevProperty.amenities.filter((_, i) => i !== index),
    }));
  };

  useEffect(() => {
    const fetchLocalities = async () => {
      try {
        const res = await ApiService.get("localities");
        if (!res.data.error) {
          setLocalities(res.data.meta);
        }
      } catch (err) {
        console.error("Error fetching localities:", err);
        toast.error("Failed to load localities");
      }
    };
    fetchLocalities();
  }, []);

  return (
    <Box
      display="grid"
      gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
      sx={{
        transition: "grid-template-columns 0.3s ease-in-out",
        overflow: "hidden",
      }}
    >
      <Sidebar
        open={sidebarOpen}
        toggleDrawer={toggleDrawer}
        setOpen={setSidebarOpen}
      />
      <div>
        <AdminNavbar />
        <div className="property-cont ">
          <div className="card rounded card-add-property g-2">
            <div
              className="px-3 py-3"
              style={{
                borderBottom: "1px solid var(--bs-secondary)",
                display: "flex",
                alignItems: "center"
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  color: "#D6AD60"
                }}
              >
              </Button>
              <h5 className="mb-0">Add Property</h5>
            </div>

            <div className="card-body">

              {/* Remove input and testing the code by Khalid  31/Dec/2024 */}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="title" className="form-label">
                      Title
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.title ? "is-invalid" : ""
                        }`}
                      type="text"
                      id="title"
                      placeholder="Enter Title"
                      name="title"
                      value={property.title}
                      onChange={handleChange}
                    />
                    {errorMessages.title && (
                      <p className="error-text">{errorMessages.title}</p>
                    )}
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <textarea
                      className={`form-control ${errorMessages.description ? "is-invalid" : ""
                        }`}
                      placeholder="Enter Description"
                      id="description"
                      name="description"
                      rows="1"
                      value={property.description}
                      onChange={handleChange}
                    ></textarea>
                    {errorMessages.description && (
                      <p className="error-text">{errorMessages.description}</p>
                    )}
                  </div>

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="price" className="form-label">
                      Price
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.price ? "is-invalid" : ""
                        }`}
                      type="number"
                      placeholder="Enter Price"
                      id="price"
                      name="price"
                      value={property.price}
                      onChange={handleChange}
                    />
                    {errorMessages.price && (
                      <p className="error-text">{errorMessages.price}</p>
                    )}
                  </div>

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="projectStatus" className="form-label">
                      Project Status
                    </label>
                    <select
                      className={`customInput form-control ${errorMessages.projectStatus ? "is-invalid" : ""}`}
                      id="projectStatus"
                      name="projectStatus"
                      value={property.projectStatus}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select Project Status
                      </option>
                      <option value="upcoming">Upcoming</option>
                      <option value="completed">Completed</option>
                    </select>
                    {errorMessages.projectStatus && (
                      <p className="error-text">{errorMessages.projectStatus}</p>
                    )}
                  </div>

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="locality" className="form-label">
                      Locality
                    </label>
                    <select
                      className={`customInput form-control ${errorMessages.locality ? "is-invalid" : ""}`}
                      id="locality"
                      name="locality"
                      value={property.locality}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select Locality
                      </option>
                      {localities.map((locality) => (
                        <option key={locality._id} value={locality._id}>
                          {locality.name}
                        </option>
                      ))}
                    </select>
                    {errorMessages.locality && (
                      <p className="error-text">{errorMessages.locality}</p>
                    )}
                  </div>

                  {property.projectStatus === "completed" && (
                    <div className="col-md-4 col-sm-6 px-4 mt-1">
                      <FormControl>
                        <FormLabel
                          id="on-demand-radio-label"
                          className="form-label"
                          style={{ color: "#D6AD60" }}
                        >
                          On Demand
                        </FormLabel>

                        <RadioGroup
                          row
                          aria-labelledby="on-demand-radio-label"
                          name="onDemand"
                          value={property.onDemand}
                          onChange={(e) =>
                            setProperty({
                              ...property,
                              onDemand: e.target.value,
                            })
                          }
                        >
                          <FormControlLabel
                            value="true"
                            control={
                              <Radio
                                sx={{ color: "#D6AD60", "&.Mui-checked": { color: "#D6AD60" } }}
                              />
                            }
                            label="Yes"
                            sx={{
                              "& .Mui-checked + .MuiFormControlLabel-label": {
                                color: "#D6AD60",
                              },
                            }}
                          />
                          <FormControlLabel
                            value="false"
                            control={
                              <Radio
                                sx={{ color: "#D6AD60", "&.Mui-checked": { color: "#D6AD60" } }}
                              />
                            }
                            label="No"
                            sx={{
                              "& .Mui-checked + .MuiFormControlLabel-label": {
                                color: "#D6AD60",
                              },
                            }}
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                  )}

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="category" className="form-label">
                      Category
                    </label>
                    <select
                      className={`customInput form-control ${errorMessages.category ? "is-invalid" : ""}`}
                      id="category"
                      name="category"
                      value={property.category || ""}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select Category
                      </option>
                      <option value="DUPLEX">Duplex</option>
                      <option value="APARTMENT">Apartment</option>
                      <option value="COMMERCIAL">Commercial</option>
                      <option value="RESIDENTIAL">Residential</option>
                      <option value="PLOT">Plot/Land</option>
                      <option value="VILLA">Villa</option>
                    </select>
                  </div>


                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="builtInArea" className="form-label">
                      Build In Area
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.latitude ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="builtInArea"
                      placeholder="Enter Build up Area"
                      name="builtInArea"
                      value={property.builtInArea}
                      onChange={handleChange}
                    />
                  </div>


                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="area" className="form-label">
                      Carpet Area
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.area ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="area"
                      name="area"
                      placeholder="Enter Carpet Area"
                      value={property.area}
                      onChange={handleChange}
                    />
                    {errorMessages.area && (
                      <p className="error-text">{errorMessages.area}</p>
                    )}
                  </div>

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="totalQuantity" className="form-label">
                      Total Quantity
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.latitude ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="totalQuantity"
                      placeholder="Enter total Quantity"
                      name="totalQuantity"
                      value={property.totalQuantity}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="availableQuantity" className="form-label">
                      Available Quantity
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.latitude ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="availableQuantity"
                      placeholder="Enter available Quantity"
                      name="availableQuantity"
                      value={property.availableQuantity}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="discountPrice" className="form-label">
                      Discount Price
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.discountPrice ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="discountPrice"
                      placeholder="Enter Discount"
                      name="discountPrice"
                      value={property.discountPrice}
                      onChange={handleChange}
                    />
                    {errorMessages.discountPrice && (
                      <p className="error-text">
                        {errorMessages.discountPrice}
                      </p>
                    )}
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="floor" className="form-label">
                      Floor
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.floor ? "is-invalid" : ""
                        }`}
                      placeholder="Enter Floor"
                      type="number"
                      id="floor"
                      name="floor"
                      value={property.floor}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="city" className="form-label">
                      City
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.city ? "is-invalid" : ""
                        }`}
                      type="text"
                      id="city"
                      placeholder="Enter City"
                      name="city"
                      value={property.city}
                      onChange={handleChange}
                    />
                    {errorMessages.city && (
                      <p className="error-text">{errorMessages.city}</p>
                    )}
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="state" className="form-label">
                      State
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.state ? "is-invalid" : ""
                        }`}
                      type="text"
                      id="state"
                      placeholder="Enter State"
                      name="state"
                      value={property.state}
                      onChange={handleChange}
                    />
                    {errorMessages.state && (
                      <p className="error-text">{errorMessages.state}</p>
                    )}
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="country" className="form-label">
                      Country
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.country ? "is-invalid" : ""
                        }`}
                      type="text"
                      id="country"
                      name="country"
                      placeholder="Enter Country"
                      value={property.country}
                      onChange={handleChange}
                    />
                    {errorMessages.country && (
                      <p className="error-text">{errorMessages.country}</p>
                    )}
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="pincode" className="form-label">
                      Pincode
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.pincode ? "is-invalid" : ""
                        }`}
                      placeholder="Enter Pincode"
                      type="text"
                      id="pincode"
                      name="pincode"
                      value={property.pincode}
                      onChange={handleChange}
                    />
                    {errorMessages.pincode && (
                      <p className="error-text">{errorMessages.pincode}</p>
                    )}
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="latitude" className="form-label">
                      Latitude
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.latitude ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="latitude"
                      placeholder="Enter Latitude"
                      name="latitude"
                      value={property.latitude}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="longitude" className="form-label">
                      Longitude
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.longitude ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="longitude"
                      placeholder="Enter Longitude"
                      name="longitude"
                      value={property.longitude}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="numberOfBedrooms" className="form-label">
                      Number of Bedrooms
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.numberOfBedrooms ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="numberOfBedrooms"
                      placeholder="Enter Number"
                      name="numberOfBedrooms"
                      value={property.numberOfBedrooms}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="numberOfBathrooms" className="form-label">
                      Number of Bathrooms
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.numberOfBathrooms ? "is-invalid" : ""
                        }`}
                      type="number"
                      id="numberOfBathrooms"
                      placeholder="Enter Number"
                      name="numberOfBathrooms"
                      value={property.numberOfBathrooms}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="constructionYear" className="form-label">
                      Construction Year
                    </label>
                    <input
                      className={`customInput form-control ${errorMessages.constructionYear ? "is-invalid" : ""
                        }`}
                      type="number"
                      placeholder="Enter Construction Year"
                      id="constructionYear"
                      name="constructionYear"
                      value={property.constructionYear}
                      onChange={handleChange}
                      min="0"
                    />
                    {errorMessages.constructionYear && (
                      <p className="error-text">
                        {errorMessages.constructionYear}
                      </p>
                    )}
                  </div>

                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label className="form-label">Upload Brochure (PDF only)</label>
                    <input
                      className="form-control"
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => {
                        setProperty({
                          ...property,
                          brochure: e.target.files[0],
                        });
                      }}
                    />
                  </div>



                  <div className="col-md-4 col-sm-6 px-4 mb-3 list-color">
                    <label htmlFor="nearbyPlace" className="form-label">
                      Add Nearby Place
                    </label>

                    <div className="input-group mb-3">
                      <input
                        list="placesList"
                        className="form-control"
                        placeholder="Type or select nearby places"
                        value={nearbyPlace}
                        onChange={(e) => setNearbyPlace(e.target.value)}
                      />

                      <datalist id="placesList">
                        {predefinedNearbyPlaces.map((placeOption, index) => (
                          <option key={index} value={placeOption} />
                        ))}
                      </datalist>

                      <button
                        className="btn btn-add btn-outline-secondary"
                        type="button"
                        onClick={() => {
                          const placesList = nearbyPlace
                            .split(",")
                            .map((item) => item.trim());
                          const newPlaces = placesList.filter(
                            (item) =>
                              item && !property.nearbyPlaces.includes(item)
                          );

                          if (newPlaces.length) {
                            setProperty((prevProperty) => ({
                              ...prevProperty,
                              nearbyPlaces: [
                                ...prevProperty.nearbyPlaces,
                                ...newPlaces,
                              ],
                            }));
                            setNearbyPlace("");
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>

                    {/* Display added nearby places by Khalid */}
                    <ul>
                      {property.nearbyPlaces.map((placeItem, index) => (
                        <li key={index}>
                          {placeItem}
                          <IconButton
                            size="small"
                            onClick={() => handleRemovePlace(index)}
                            aria-label="remove"
                            style={{ marginLeft: "10px" }}
                          >
                            <CloseIcon
                              style={{
                                color: "var(--bs-accent)",
                                fontSize: "17px",
                              }}
                            />
                          </IconButton>
                        </li>
                      ))}
                    </ul>
                  </div>




                  <div className="col-md-4 col-sm-6 px-4 mb-3 list-color">
                    <label htmlFor="amenity" className="form-label">
                      Add Amenity
                    </label>

                    <div className="input-group mb-3">
                      <input
                        list="amenitiesList"
                        className="form-control"
                        placeholder="Type or select amenities"
                        value={amenity}
                        onChange={(e) => setAmenity(e.target.value)}
                      />

                      <datalist id="amenitiesList">
                        {predefinedAmenities.map((amenityOption, index) => (
                          <option key={index} value={amenityOption} />
                        ))}
                      </datalist>

                      <button
                        className="btn btn-add btn-outline-secondary"
                        type="button"
                        onClick={() => {
                          const amenityList = amenity
                            .split(",")
                            .map((item) => item.trim());
                          const newAmenities = amenityList.filter(
                            (item) => item && !property.amenities.includes(item)
                          );

                          if (newAmenities.length) {
                            setProperty((prevProperty) => ({
                              ...prevProperty,
                              amenities: [
                                ...prevProperty.amenities,
                                ...newAmenities,
                              ],
                            }));
                            setAmenity("");
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>

                    <ul>
                      {property.amenities.map((amenityItem, index) => (
                        <li key={index}>
                          {amenityItem}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveAmenity(index)}
                            aria-label="remove"
                            style={{ marginLeft: "10px" }}
                          >
                            <CloseIcon
                              style={{
                                color: "var(--bs-accent)",
                                fontSize: "17px",
                              }}
                            />
                          </IconButton>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* <div
                    className="col-md-4 col-sm-6 px-4 mb-3"
                    style={{ display: "none" }}
                  >
                    <label htmlFor="images360" className="form-label">
                      Upload 360 Image
                    </label>
                    <input
                      className="form-control"
                      type="file"
                      id="images360"
                      name="images360"
                      onChange={handle360ImageChange}
                    />
                  </div> */}

                  {/* <div
                    className="col-md-4 col-sm-6 px-4 mb-3"
                    style={{ display: "none" }}
                  >
                    <label htmlFor="threeDModels" className="form-label">
                      Upload 3D Model
                    </label>
                    <input
                      className="form-control"
                      type="file"
                      id="threeDModels"
                      name="threeDModels"
                      onChange={handle3DModelChange}
                    />
                  </div> */}


                  {/* 
                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label htmlFor="locality" className="form-label">
                    Locality
                    </label>
                    <select
                      className={`customInput form-control ${errorMessages.locality ? "is-invalid" : ""}`}
                      id="locality"
                      name="locality"
                      value={property.locality || ""}   
                      onChange={handleChange}          
                    >
                      <option value="" disabled>
                        Select Category
                      </option>
                      <option value="DUPLEX">Duplex</option>
                      <option value="APARTMENT">Apartment</option>
                      <option value="COMMERCIAL">Commercial</option>
                      <option value="PLOT">Plot/Land</option>
                      <option value="VILLA">Villa</option>
                    </select>
                  </div> */}







                  <div className="col-md-4 col-sm-6 px-4 mb-3">
                    <label className="form-label">Upload Images</label>
                    <input
                      className="form-control"
                      type="file"
                      multiple
                      accept="image/png, image/jpeg"
                      onChange={handleFileChange}
                    />
                    <div className="mt-2">
                      <div className="d-flex img-add">

                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="position-relative">
                            <img
                              src={preview}
                              alt={`Preview ${index}`}
                              style={{
                                maxWidth: "100px",
                                marginRight: "10px",
                                marginBottom: "10px",
                              }}
                            />
                            <p
                              className="clear-img"
                              onClick={() => handleRemoveImage(index)}
                            >
                              x
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>


                </div>
                <button
                  type="submit"
                  className="btn btn-accent mx-3 my-2"
                  disabled={loading}
                  style={{ width: '100px' }}
                >
                  {loading ? (
                    <CircularProgress size={20} style={{ color: "#fff" }} />
                  ) : (
                    "Submit"
                  )}
                </button>


                <button
                  className="btn btn-secondary mx-3 my-2"
                  onClick={() => navigate(`/admin/adminPropertyListing`)}
                >
                  Cancel
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />

    </Box>
  );
};

export default AddPropertyForm;
